import React from 'react';
import { Avatar, ButtonBase } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box, styled } from '@mui/system';

import LinkIcon from '@mui/icons-material/Link';

import * as ItemUtils from '../../common/ItemTools';
import { useCardShows } from '../common/card/useCardDensity';
import useMetaFit, { META_AGE_CLASS, META_DURATION_CLASS, META_NAME_CLASS } from './useMetaFit';
import { SANS_STACK } from '../common/tokens';
import { AuthorHeader, AuthorNameHover } from './Authors';
import { SiteFavicon, SiteNameHover } from './Site';
import { domainLogo, UNBRANDED_BG, UNBRANDED_COLOR } from '../common/brands';
import { openCustomUrl } from './utils';

const MetaRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  color: '#666',
  marginBottom: 10,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  fontFamily: SANS_STACK,
  // useMetaFit's verdict: the credit needs the room a time figure was taking,
  // the age before the duration.
  [`&[data-crowded="1"] .${META_AGE_CLASS}`]: { display: 'none' },
  [`&[data-crowded="2"] .${META_AGE_CLASS}`]: { display: 'none' },
  [`&[data-crowded="2"] .${META_DURATION_CLASS}`]: { display: 'none' },
});

// One time figure and the separator that introduces it, so they leave together.
const TimeFact = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
});

const META_NAME_SX = {
  color: '#1a1a1a',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flexShrink: 1,
  minWidth: 0,
};

const MetaSeparator = styled('span')({
  color: '#c0c0c0',
});

const LinkAvatar = styled(Avatar)({
  width: 18,
  height: 18,
});

// A discussion link opens in a new tab; the card's own click handlers must not
// also fire.
function renderLinkChip(link) {
  const src = domainLogo(ItemUtils.getDomainFromUrl(link.url));
  return (
    <ButtonBase
      key={link.url}
      onClick={(e) => { e.stopPropagation(); openCustomUrl(link.url); }}
    >
      {src
        ? <LinkAvatar src={src} variant="rounded" />
        : (
          <LinkAvatar variant="rounded" sx={{ bgcolor: UNBRANDED_BG, color: UNBRANDED_COLOR }}>
            <LinkIcon sx={{ fontSize: 12 }} />
          </LinkAvatar>
        )}
    </ButtonBase>
  );
}

/**
 * The line above the title: site favicon, whoever gets credit, the item's age
 * and length, and the off-site discussion links.
 *
 * Credit goes to the authors when the item has any and falls back to the site.
 * Zen mode keeps the credit and drops the two time figures, and so does a card
 * with no room for them — the credit is the last thing on the row to go.
 */
const CardMeta = React.forwardRef(({
  authors, titleAuthorName, site, siteId, domain, logoUrl, sourceLetter,
  metrics, links, extraLinks,
  onAuthorView, onAuthorFilter, onSiteView, onSiteFilter,
}, ref) => {
  const theme = useTheme();
  const shows = useCardShows();

  const firstAuthor = (authors && authors[0]) || null;
  const titleAuthorId = firstAuthor ? firstAuthor.id : null;
  const allAuthors = (authors && authors.length > 1)
    ? authors.map((a) => ({
      id: a.id,
      name: ItemUtils.getAuthorName(a),
      imageUrl: ItemUtils.getAuthorImageUrl(a.privateData, a.imageScope)
        || ItemUtils.getAuthorImageUrl(a.publicData, 'public'),
      author: a,
    }))
    : null;
  const singleAuthor = (authors && authors.length === 1) ? authors[0] : null;

  const sourceName = titleAuthorName || domain || '';
  // The ladder decides whether the card has room for a time figure at all;
  // the row decides whether the credit can spare it.
  const showAge = shows('age');
  const showDuration = shows('duration');
  const ageLabel = metrics.published || metrics.created;
  const externalLinks = [
    ...(links || []).filter((l) => !l.primary && l.visible),
    ...(extraLinks || []),
  ];
  const hasExternalLinks = externalLinks.length > 0;

  const { rowRef, crowdLevel } = useMetaFit(
    `${sourceName}|${ageLabel}|${metrics.length}|${externalLinks.length}`
    + `|${showAge}|${showDuration}|${shows('links')}`,
  );
  // The row is measured here and by the card's text fit, so the node the
  // parent holds is this one.
  React.useImperativeHandle(ref, () => rowRef.current, [rowRef]);

  // The meta row credits the author when there is one; otherwise the name slot
  // falls back to the site and behaves like one.
  let nameSlot;
  if (allAuthors) {
    nameSlot = (
      <AuthorHeader
        className={META_NAME_CLASS}
        authors={allAuthors}
        onAuthorClick={onAuthorView}
        onAuthorFilter={onAuthorFilter}
      />
    );
  } else if (singleAuthor) {
    nameSlot = (
      <AuthorNameHover
        className={META_NAME_CLASS}
        author={singleAuthor}
        id={titleAuthorId}
        name={sourceName}
        clickable={Boolean(titleAuthorId && onAuthorView)}
        onAuthorClick={onAuthorView}
        onAuthorFilter={onAuthorFilter}
        sx={{
          ...META_NAME_SX,
          cursor: titleAuthorId ? 'pointer' : 'default',
          '&:hover': titleAuthorId ? { color: theme.palette.primary.main } : {},
        }}
      />
    );
  } else {
    nameSlot = (
      <SiteNameHover
        className={META_NAME_CLASS}
        site={site}
        id={siteId}
        domain={domain}
        name={sourceName}
        onSiteView={onSiteView}
        onSiteFilter={onSiteFilter}
        sx={{
          ...META_NAME_SX,
          '&:hover': siteId && onSiteView ? { color: theme.palette.primary.main } : {},
        }}
      />
    );
  }

  if (!shows('credit') && !showAge && !showDuration && !shows('links')) return null;

  return (
    <MetaRow ref={rowRef} data-crowded={String(crowdLevel)}>
      {shows('credit') && (
        <>
          <SiteFavicon
            site={site}
            id={siteId}
            domain={domain}
            logoUrl={logoUrl}
            letter={sourceLetter}
            onSiteView={onSiteView}
            onSiteFilter={onSiteFilter}
          />
          {nameSlot}
        </>
      )}
      {showAge && ageLabel && (
        <TimeFact className={META_AGE_CLASS}>
          <MetaSeparator>·</MetaSeparator>
          <Box component="span" sx={{ color: '#666' }}>{ageLabel}</Box>
        </TimeFact>
      )}
      {showDuration && metrics.length && (
        <TimeFact className={META_DURATION_CLASS}>
          <MetaSeparator>·</MetaSeparator>
          <Box component="span" sx={{ color: '#666' }}>{metrics.length}</Box>
        </TimeFact>
      )}
      {shows('links') && hasExternalLinks && (
        <Box sx={{ ml: 'auto', display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            {externalLinks.map(renderLinkChip)}
          </Box>
        </Box>
      )}
    </MetaRow>
  );
});

export default CardMeta;
